<script setup lang="ts">
import SpinnerIcon from '@assets/icons/spinner.svg?component';
import { BaseButton, BaseInput } from '@components/base';
import { AUTH_ROUTES, buildLoginUrl } from '@constants/auth-routes';
import { getSafeLoginReturnUrl } from '@helpers/auth-redirect';
import { ResetPasswordPayload } from '@models/user';
import { useUserStore } from '@stores/index';
import { useVuelidate } from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';
import { reactive, computed, ref, onMounted } from 'vue';

const props = withDefaults(
  defineProps<{
    content?: Record<string, string>;
    inDrawer?: boolean;
    initialSuccess?: boolean;
  }>(),
  { content: () => ({}), inDrawer: false, initialSuccess: false },
);

const signInUrl = computed(() => buildLoginUrl(getSafeLoginReturnUrl()));

const emit = defineEmits<{ close: []; submitted: [] }>();

const userStore = useUserStore();
const formData = reactive<ResetPasswordPayload>({
  loginName: '',
});

const rules = {
  loginName: { email, required },
};

const v$ = useVuelidate(rules, formData);

const title = computed(() => props.content?.title ?? 'Forgot Password');
const instruction = computed(() => props.content?.instruction ?? 'Enter your email and we\'ll send a link to reset your password');
const backToLoginText = computed(() => props.content?.backToLogin ?? 'Back to login');
const successTitle = computed(() => props.content?.successTitle ?? 'Check your email');
const successInstruction = computed(() => props.content?.successInstruction ?? 'You should receive an email shortly with a link to reset your password');
const didntReceiveText = computed(() => props.content?.didntReceive ?? 'Didn\'t receive it?');
const resendText = computed(() => props.content?.resend ?? 'Resend link');
const loginNameError = computed(() => v$.value.loginName.$errors[0]?.$message ?? '');
const isSuccess = ref(props.initialSuccess);
const submittedEmail = ref('');

const onSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  const result = await userStore.resetPassword(formData);
  if (result) {
    submittedEmail.value = formData.loginName.trim();
    sessionStorage.setItem('auth_reset_email', submittedEmail.value);
    if (props.inDrawer) {
      isSuccess.value = true;
      emit('submitted');
      return;
    }
    window.location.assign(AUTH_ROUTES.RESET_LINK);
  }
};

const onResendLink = async () => {
  if (!submittedEmail.value || userStore.loading) return;
  await userStore.resetPassword({ loginName: submittedEmail.value });
};

const onBackToLogin = () => {
  if (props.inDrawer) {
    emit('close');
  } else {
    window.location.assign(signInUrl.value);
  }
};

onMounted(() => {
  if (props.initialSuccess) {
    submittedEmail.value = sessionStorage.getItem('auth_reset_email') ?? '';
    isSuccess.value = true;
  }
});
</script>

<template>
  <form
    v-if="!isSuccess"
    class="flex flex-col gap-24"
    @submit.prevent="onSubmit"
  >
    <h1
      v-if="!inDrawer"
      class="font-inter-tight text-20 font-semibold text-off-black-500 lg:text-26"
    >
      {{ title }}
    </h1>

    <p class="text-14 font-[450] text-off-black-500">
      {{ instruction }}
    </p>

    <div class="flex flex-col gap-16">
      <BaseInput
        :model-value="formData.loginName"
        type="email"
        placeholder="Email"
        autocomplete="email"
        :error="v$.loginName.$error"
        :error-message="(loginNameError as unknown) as string"
        @update:model-value="formData.loginName = $event.trim()"
      />
    </div>

    <BaseButton
      text="RESET PASSWORD"
      type="submit"
      variant="primary"
      class="!flex !h-[44px] !min-h-[44px] !w-full items-center justify-center !rounded-full !py-0 !text-16 !font-medium !uppercase"
      :loading="userStore.loading"
      :disabled="v$.$error"
    >
      <template #icon-right>
        <span
          class="size-20 opacity-0"
          aria-hidden="true"
        />
      </template>
    </BaseButton>

    <p class="text-center text-14 text-off-black-500">
      Remember your password?
      <a
        v-if="!inDrawer"
        :href="signInUrl"
        class="font-medium text-teal-500 hover:underline"
      >
        {{ backToLoginText }}
      </a>
      <button
        v-else
        type="button"
        class="font-medium text-teal-500 hover:underline"
        @click="onBackToLogin"
      >
        {{ backToLoginText }}
      </button>
    </p>
  </form>

  <div
    v-else
    class="flex flex-col gap-28"
  >
    <div>
      <h2 class="text-18 font-[400] text-off-black-500">
        {{ successTitle }}
      </h2>

      <p class="mt-16 text-14 font-[400] text-[#6C6A66]">
        {{ successInstruction }}
      </p>
    </div>

    <p class="text-14 text-off-black-500">
      {{ didntReceiveText }}
      <button
        type="button"
        class="inline-flex items-center gap-8 font-medium text-teal-500 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="userStore.loading"
        @click="onResendLink"
      >
        {{ resendText }}
        <SpinnerIcon
          v-if="userStore.loading"
          class="size-14 animate-spin"
        />
      </button>
    </p>

    <BaseButton
      text="BACK TO LOGIN"
      type="button"
      variant="primary"
      class="!flex !h-[44px] !min-h-[44px] !w-full items-center justify-center !rounded-full !py-0 !text-16 !font-medium !uppercase"
      :loading="userStore.loading"
      @click="onBackToLogin"
    >
      <template #icon-right>
        <span
          class="size-20 opacity-0"
          aria-hidden="true"
        />
      </template>
    </BaseButton>
  </div>
</template>
